import React from "react";
import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SEOHead } from "@/components/SEOHead";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    const handleUnsubscribe = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Invalid unsubscribe link. The token is missing.");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("newsletter-unsubscribe", {
          body: { token },
        });

        if (error) throw error;

        setStatus("success");
        setMessage(data.message || "You've been unsubscribed successfully.");
        setEmail(data.email || "");
      } catch (error) {
        console.error("Unsubscribe error:", error);
        setStatus("error");
        setMessage(
          error instanceof Error 
            ? error.message 
            : "Failed to unsubscribe. Please try again or contact support."
        );
      }
    };

    handleUnsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <SEOHead
        title="Unsubscribe - DayOne"
        description="Unsubscribe from DayOne newsletter"
      />

      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          {status === "loading" && (
            <>
              <div className="mx-auto mb-4">
                <Loader2 className="h-12 w-12 text-primary animate-spin" />
              </div>
              <CardTitle>Processing your request...</CardTitle>
              <CardDescription>Please wait while we unsubscribe you.</CardDescription>
            </>
          )}

          {status === "success" && (
            <>
              <div className="mx-auto mb-4">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              </div>
              <CardTitle>Unsubscribed Successfully</CardTitle>
              <CardDescription>{message}</CardDescription>
            </>
          )}

          {status === "error" && (
            <>
              <div className="mx-auto mb-4">
                <XCircle className="h-12 w-12 text-destructive" />
              </div>
              <CardTitle>Unsubscribe Failed</CardTitle>
              <CardDescription className="text-destructive">{message}</CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {status === "success" && email && (
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-center">
                <span className="font-medium">{email}</span> has been removed from our mailing list.
              </p>
            </div>
          )}

          <div className="text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              {status === "success" 
                ? "We're sorry to see you go. You won't receive any more emails from us."
                : status === "error"
                ? "If you continue to have issues, please contact us at hello@dayone.ai"
                : ""}
            </p>

            {status === "success" && (
              <div className="pt-4">
                <Button asChild variant="outline">
                  <Link to="/">Return to Home</Link>
                </Button>
              </div>
            )}

            {status === "error" && (
              <div className="pt-4">
                <Button asChild>
                  <Link to="/">Go to Homepage</Link>
                </Button>
              </div>
            )}
          </div>

          {status === "success" && (
            <div className="border-t pt-4 mt-6">
              <p className="text-xs text-center text-muted-foreground">
                Changed your mind?{" "}
                <Link to="/" className="text-primary hover:underline">
                  Subscribe again
                </Link>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Unsubscribe;
